import { createAction as createSmartAction } from 'redux-smart-actions';
import { RequestAction, QueryState, stopPolling, resetRequests } from '@redux-requests/core';
import {
  DocumentType,
  DocumentTypes,
  FileStatus,
  UpdateInspectionData,
  InspectFileState,
  PendingIdUpdate,
  PendingFileMapping,
} from 'types';
import qs from 'qs';
import { AxiosError, AxiosProgressEvent } from 'axios';
import {
  files,
  app,
  getOCRStatus,
  getInspectionId,
  getUpdateInspectionData,
  inspection,
  getShapesJobStatus,
} from 'store';
import { fetchFile, preprocessFile } from 'store/requests';
import {
  FailedFile,
  FetchMultipleFilesData,
  UploadedFile,
  ApiUploadMultipleFiles,
  ApiFetchMultipleFiles,
  ApiPollFilesStatus,
  ApiMergeFiles,
  APIStartOCR,
  APIFetchOCR,
} from './types';
import { FileErrors, FileFlags } from '@verify/shared-utils';
import store, { axiosInstance } from 'store/store';
import { Store } from '@reduxjs/toolkit';
import { updateInspectionFiles } from '../inspections/actions';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import { PDFManagerFactory } from 'pdftron';
import { isInspectionUrl } from 'utils/location';

type FetchMultipleFilesPayload = [
  string[], // uploaded file ids
  FailedFile[], // files rejected from the client by useDropzone (used for storing data in this action)
  DocumentTypes, // document type
];
export const fetchMultipleFiles = createSmartAction<
  RequestAction<ApiFetchMultipleFiles, FetchMultipleFilesData | undefined>,
  FetchMultipleFilesPayload
>('files/fetchMultipleFiles', (fileUploadIds, rejectedFiles, documentType) => {
  const fileParams = qs.stringify({ fileIds: fileUploadIds }, { indices: false });
  return {
    request: {
      url: `/files?${fileParams}`,
      method: 'GET',
    },
    meta: {
      errorMessage: 'There was an error uploading files. Please try again.',
      getData: (data) => {
        // we wait until we get the response for each file sent for upload
        const uploadedFiles: UploadedFile[] = [];
        const failedFiles: FailedFile[] = [...rejectedFiles]; // we initialize the rejected files with the files that were rejected form the client
        data.forEach((file) => {
          if (file.status === FileStatus.error) {
            // append file to rejected files list
            const failedFile: FailedFile = {
              id: file.id,
              filename: file.originalFilename,
              error: file.error || FileErrors.uploadError,
            };
            failedFiles.push(failedFile);
          } else {
            // append file to successfully uploaded files list
            const uploadedFile: UploadedFile = {
              id: file.id,
              originalFilename: file.originalFilename,
              originalPath: file.originalPath,
              filename: file.filename,
              path: file.path,
              signedUrl: file.signedUrl,
              createdAt: file.createdAt,
              updatedAt: file.updatedAt,
              status: file.status,
            };
            uploadedFiles.push(uploadedFile);
          }
        });
        return { uploadedFiles, failedFiles, documentType };
      },
      onError: (error: AxiosError, _action, store) => {
        store.dispatch(files.actions.setMultipleFileStatus({ documentType, status: false }));
        return error;
      },
      onSuccess: (response: QueryState<FetchMultipleFilesData>, _requestAction, store) => {
        // Filter out cancelled files
        const { cancelledFileIds, cancelledFilenames } = store.getState().files;

        const filteredResponse = {
          ...response,
          data: {
            ...response.data,
            uploadedFiles: response.data.uploadedFiles.filter(
              (f) => !cancelledFileIds.includes(f.id) && !cancelledFilenames.includes(f.originalFilename),
            ),
            failedFiles: response.data.failedFiles.filter(
              (f) => !cancelledFileIds.includes(f.id) && !cancelledFilenames.includes(f.filename),
            ),
          },
        };

        const failedFilesCount = filteredResponse.data.failedFiles.length;
        if (failedFilesCount > 0) {
          store.dispatch(
            app.actions.setSnackMessage({
              message: `There was an error uploading ${failedFilesCount} of your files. Please try again.`,
            }),
          );
        }
        store.dispatch(files.actions.setMultipleFileStatus({ documentType, status: false }));
        return filteredResponse;
      },
      onAbort: (_action, store) => {
        store.dispatch(files.actions.setMultipleFileStatus({ documentType, status: false }));
      },
    },
  };
});

type deleteFileByIdPayload = [
  string, // file Id
];
export const deleteFileById = createSmartAction<RequestAction<boolean>, deleteFileByIdPayload>(
  'files/deleteFileById',
  (fileId) => ({
    request: {
      url: `/files/${fileId}`,
      method: 'DELETE',
    },
  }),
);

type DeleteFilesPayload = [
  string[], // file Ids
];
export const deleteFiles = createSmartAction<RequestAction<boolean>, DeleteFilesPayload>(
  'files/deleteFiles',
  (fileIds) => ({
    request: {
      url: `/files`,
      method: 'DELETE',
      data: { fileIds },
    },
  }),
);

type PollFilesStatusPayload = [
  string[], // uploaded file ids
  FailedFile[], // files rejected from the client by useDropzone (used for storing data in this action)
  DocumentTypes, // document type
];
export const pollFilesStatus = createSmartAction<RequestAction<boolean>, PollFilesStatusPayload>(
  'files/pollFilesStatus',
  (fileUploadIds, rejectedFiles, documentType) => {
    const fileParams = qs.stringify({ fileIds: fileUploadIds }, { indices: false });
    return {
      request: {
        url: `/files/status?${fileParams}`,
        method: 'GET',
      },
      meta: {
        poll: 2,
        errorMessage: 'There was an error uploading files. Please try again.',
        onError: (error: AxiosError, _action, store) => {
          store.dispatch(stopPolling([pollFilesStatus.toString()]));
          store.dispatch(files.actions.setMultipleFileStatus({ documentType, status: false }));
          store.dispatch(files.actions.clearPendingReorderData());
          return error;
        },
        onSuccess: (response: QueryState<ApiPollFilesStatus>, _requestAction, store) => {
          if (response.data === true) {
            // we stop polling once we have the response for each file upload in batch and is finished converting
            store.dispatch(stopPolling([pollFilesStatus.toString()]));
            if (store.getState().files.isCreationCancelled === false) {
              store.dispatch(fetchMultipleFiles(fileUploadIds, rejectedFiles, documentType));
            }
          }
          return response;
        },
        onAbort: (_action, store) => {
          store.dispatch(files.actions.setMultipleFileStatus({ documentType, status: false }));
        },
      },
    };
  },
);

type PollFileStatusPayload = [
  string, // uploaded file ids
  DocumentTypes, // document type
];
// I'm creating a new action even though it's almost the same because the other one is used in the multiple file upload
// flow and it's a bit different
export const pollFileStatus = createSmartAction<RequestAction<boolean>, PollFileStatusPayload>(
  'files/pollFileStatus',
  (fileUploadIds, documentType) => {
    const fileParams = qs.stringify({ fileIds: [fileUploadIds] }, { indices: false });
    const requestKey = fileUploadIds;
    const query = pollFileStatus.toString() + requestKey;

    return {
      request: {
        url: `/files/status?${fileParams}`,
        method: 'GET',
      },
      meta: {
        requestKey,
        poll: 2,
        errorMessage: 'There was an error uploading files. Please try again.',
        onError: (error: AxiosError, _action, store) => {
          store.dispatch(stopPolling([query]));
          return error;
        },
        onSuccess: (response: QueryState<ApiPollFilesStatus>, _requestAction, store) => {
          if (response.data === true) {
            // we stop polling once the file is completed or error
            store.dispatch(stopPolling([query]));
            if (store.getState().files.isCreationCancelled === false) {
              store.dispatch(fetchFile(fileUploadIds, documentType, true));
            }
          }
          return response;
        },
        onAbort: (_action, store) => {
          store.dispatch(stopPolling([query]));
          store.dispatch(files.actions.removeFile({ documentType }));
        },
      },
    };
  },
);

type PollMergeStatusPayload = [
  InspectFileState, // merged file
  DocumentTypes, // document type
];
export const pollMergeStatus = createSmartAction<RequestAction<boolean>, PollMergeStatusPayload>(
  'files/pollMergeStatus',
  (mergedFile, documentType) => {
    const fileParams = qs.stringify({ fileIds: [mergedFile.fileId] }, { indices: false });
    return {
      request: {
        url: `/files/status?${fileParams}`,
        method: 'GET',
      },
      meta: {
        poll: 2,
        errorMessage: 'There was an error while merging your files. Check your files, and please try again.',
        onError: (error: AxiosError, _action, store) => {
          store.dispatch(stopPolling([pollMergeStatus.toString()]));
          store.dispatch(files.actions.setMultipleFileStatus({ documentType, status: false }));
          return error;
        },
        onSuccess: (response: QueryState<ApiPollFilesStatus>, _requestAction, store) => {
          if (response.data === true) {
            // we stop polling once we have the response for file merge and send the file for preprocessing
            store.dispatch(stopPolling([pollMergeStatus.toString()]));
            if (store.getState().files.isCreationCancelled === false) {
              store.dispatch(preprocessFile(mergedFile, documentType));
            }
          }
          return response;
        },
      },
    };
  },
);

type UploadMultipleFilesPayload = [
  FormData, // accepted files
  FailedFile[], // rejected file names
  DocumentType,
  (progressEvent: AxiosProgressEvent) => void, // upload progress callback
  removeHeadersFooters?: boolean,
  autoSelectLayersSeparations?: boolean,
  pendingMapping?: PendingFileMapping[], // mapping of temp IDs to filenames for updating pending files
];
export const startBatchFileCreation = createSmartAction<
  RequestAction<ApiUploadMultipleFiles>,
  UploadMultipleFilesPayload
>(
  'files/startBatchFileCreation',
  (
    acceptedFiles,
    rejectedFiles,
    documentType,
    uploadProgressUpdate,
    removeHeadersFooters,
    autoSelectLayersSeparations,
    pendingMapping,
  ) => {
    const filesData = (acceptedFiles.getAll('files') || []) as File[];
    const checksums = (acceptedFiles.getAll('checksums') || []) as string[];
    const data = filesData.map((file, i) => {
      const fileName = file instanceof File ? file.name : '';
      const fileType = file instanceof File ? file.type : '';
      return {
        fileName,
        fileType,
        checksum: checksums[i],
        removeHeadersFooters,
        autoSelectLayersSeparations,
      };
    });
    return {
      request: {
        url: `/files/startBatchFileCreation`,
        method: 'POST',
        data: { files: data },
        onUploadProgress: uploadProgressUpdate,
      },
      meta: {
        document: documentType,
        fileName: data.map((file) => file.fileName).join(','),
        fileCount: data.length,
        removeHeadersFooters,
        errorMessage: 'There was an error uploading files. Please try again.',
        onRequest: (request, _action, store) => {
          store.dispatch(files.actions.setMultipleFileStatus({ documentType, status: 'UPLOADING' }));
          store.dispatch(resetRequests([fetchMultipleFiles.toString()]));

          // Remove these filenames from cancelled lists (in case user is re-uploading)
          const filenames = data.map((file) => file.fileName);
          store.dispatch(files.actions.removeCancelledFilenames(filenames));

          return request;
        },
        onSuccess: (response: any, _requestAction, store) => {
          const fileData = response.data;

          // If pending mapping provided, create ID mapping and dispatch updates
          if (pendingMapping && pendingMapping.length > 0) {
            const usedTempIds = new Set<string>();
            const idUpdates = fileData
              .map((apiFile: any) => {
                // Match by filename, but track which temp IDs we've used (for same-name files)
                const mapping = pendingMapping.find(
                  (m) => m.filename === apiFile.fileName && !usedTempIds.has(m.tempId),
                );
                if (mapping) {
                  usedTempIds.add(mapping.tempId);
                  return {
                    tempId: mapping.tempId,
                    realId: apiFile.id,
                  };
                }
                return null;
              })
              .filter(Boolean) as PendingIdUpdate[];

            if (idUpdates.length > 0) {
              store.dispatch(files.actions.addPendingIdUpdates(idUpdates));
            }
          }

          const awsUploadPromises = fileData.map((file: any, index: number) => {
            return axiosInstance.put(file.signedURL, filesData[index], {
              headers: {
                'Content-Type': data[index].fileType,
              },
            });
          });
          Promise.all(awsUploadPromises)
            .then((resp) => {
              const { cancelledFileIds, cancelledFilenames } = store.getState().files;

              const uploadedFiles = resp
                .map((awsResp, index) => {
                  if (awsResp.status !== 200) {
                    rejectedFiles.push({
                      id: fileData[index].id,
                      filename: fileData[index].fileName,
                      error: FileErrors.uploadError,
                    });
                  }
                  return {
                    id: fileData[index].id,
                    status: awsResp.status,
                    filename: fileData[index].fileName,
                  };
                })
                .filter((file) => !cancelledFileIds.includes(file.id) && !cancelledFilenames.includes(file.filename)); // Filter out individually cancelled files by ID or filename

              if (store.getState().files.isCreationCancelled === false && uploadedFiles.length > 0) {
                store.dispatch(finishBatchFileCreation(uploadedFiles, documentType, rejectedFiles));
              }
            })
            .catch((error) => {
              // Handle S3 upload errors
              console.error('S3 upload failed:', error);
              // Notify component to remove pending files if mapping provided
              if (pendingMapping && pendingMapping.length > 0) {
                const tempIds = pendingMapping.map((m) => m.tempId);
                const filenames = pendingMapping.map((m) => m.filename);
                store.dispatch(files.actions.addFailedUpload({ fileIds: tempIds, filenames }));
              }
              store.dispatch(files.actions.setMultipleFileStatus({ documentType, status: false }));
              store.dispatch(files.actions.clearPendingReorderData());
              store.dispatch(
                app.actions.setSnackMessage({
                  message: 'There was an error uploading files to storage. Please try again.',
                  type: 'error',
                }),
              );
            });
        },
        onError: (error, _action, store) => {
          // Notify component to remove pending files if mapping provided
          if (pendingMapping && pendingMapping.length > 0) {
            const tempIds = pendingMapping.map((m) => m.tempId);
            const filenames = pendingMapping.map((m) => m.filename);
            store.dispatch(files.actions.addFailedUpload({ fileIds: tempIds, filenames }));
          }
          store.dispatch(files.actions.setMultipleFileStatus({ documentType, status: false }));
          store.dispatch(files.actions.clearPendingReorderData());
          return error;
        },
        onAbort: (_action, store) => {
          store.dispatch(files.actions.setMultipleFileStatus({ documentType, status: false }));
          store.dispatch(files.actions.clearPendingReorderData());
        },
      },
    };
  },
);

export const finishBatchFileCreation = createSmartAction<
  RequestAction<{ files: { id: string; status: number }[]; documentType: DocumentType; rejectedFiles: FailedFile[] }>
>('request/finishBatchFileCreation', (files, document, rejectedFiles) => {
  return {
    payload: {
      request: {
        url: `/files/finishBatchFileCreation`,
        method: 'PATCH',
        data: {
          files,
        },
      },
    },
    meta: {
      errorMessage: 'There was an error finishing the file creation. Please try again.',
      onSuccess: (_response: any, _requestAction: any, store: Store) => {
        const fileIds = files.map((file: any) => file.id);
        store.dispatch(pollFilesStatus(fileIds, rejectedFiles, document));
      },
      onError: (error, _action, store) => {
        store.dispatch(files.actions.setMultipleFileStatus({ documentType: document, status: false }));
        store.dispatch(files.actions.clearPendingReorderData());
        return error;
      },
    },
  };
});

type MergeMultipleFilesPayload = [
  string[], // file Ids
  DocumentType,
];
export const mergeFiles = createSmartAction<RequestAction<ApiMergeFiles>, MergeMultipleFilesPayload>(
  'files/mergeFiles',
  (fileIds, documentType) => ({
    request: {
      url: `/files/merge`,
      method: 'POST',
      data: { fileArray: fileIds },
    },
    meta: {
      errorMessage: 'There was an error while merging your files. Check your files, and please try again.',
      onRequest: (request, _action, store) => {
        store.dispatch(files.actions.setMultipleFileStatus({ documentType, status: 'MERGING' }));
        return request;
      },
      onSuccess: (response: QueryState<ApiMergeFiles>, _requestAction, store) => {
        const { id, filename, status, createdAt } = response.data;
        const mergedFile = {
          fileId: id,
          url: '',
          originalName: '',
          fileName: filename,
          status,
          createdAt,
          mergedFileData: undefined,
        };

        store.dispatch(
          files.actions.setFile({
            fileState: mergedFile,
            documentType,
            isNewFile: true,
          }),
        );
        store.dispatch(pollMergeStatus(mergedFile, documentType));
        return response;
      },
      onError: (error, _action, store) => {
        store.dispatch(files.actions.setMultipleFileStatus({ documentType, status: false }));
        return error;
      },
    },
  }),
);

type FetchOCRPayload = [string, DocumentType];
export const fetchOCR = createSmartAction<RequestAction<APIFetchOCR>, FetchOCRPayload>(
  'request/fetchOCRFiles',
  (fileId, documentType) => {
    const state = store.getState();
    const inspectionId: string = getInspectionId(state);

    return {
      payload: {
        request: {
          url: inspectionId ? `files/${fileId}?inspectionId=${inspectionId}` : `files/${fileId}`,
          method: 'GET',
        },
      },
      meta: {
        errorMessage: 'There was an error. Please try again.',
        onSuccess: (response: QueryState<APIFetchOCR>, _requestAction, store) => {
          const file = response.data;

          if (file.status === FileStatus.error) {
            store.dispatch(
              app.actions.setSnackMessage({
                message: 'There was an error loading the results. Please try again',
                type: 'error',
              }),
            );
          } else {
            store.dispatch(app.actions.setShowLiveText(false));
            store.dispatch(
              files.actions.removeFile({
                documentType,
              }),
            );
            store.dispatch(
              files.actions.setFile({
                fileState: {
                  fileId: file.id,
                  url: file.signedUrl,
                  originalName: file.originalFilename,
                  fileName: file.filename,
                  status: file.status,
                  createdAt: file.createdAt,
                  mergedFileData: file.mergedMetadata,
                  isOCR: file.flags.includes(FileFlags.ocr),
                  fileMetadata: file.fileMetadata,
                },
                documentType,
                isNewFile: true,
              }),
            );
            // clear inspection layers and separations
            store.dispatch(inspection.actions.setLayers({ documentType, layers: [] }));
            store.dispatch(inspection.actions.setSeparations({ documentType, separations: [] }));

            store.dispatch(app.actions.setUpdatingInspection(true));
            const state = store.getState();
            const id: string = getInspectionId(state);
            const updateInspectionData: UpdateInspectionData = getUpdateInspectionData(state);
            const fileKey = documentType === DocumentTypes.source ? 'masterFileId' : 'sampleFileId';
            updateInspectionData[fileKey] = file.id;
            store.dispatch(updateInspectionFiles(id, updateInspectionData));

            store.dispatch(files.actions.setOCRHasRun(true));
          }
          return response;
        },
        onError: (error: AxiosError, _action, store) => {
          store.dispatch(files.actions.setOCRStatus({ documentType, status: FileStatus.error }));
          return error;
        },
      },
    };
  },
);

type PollOCRStatusPayload = [string[], DocumentType];
const pollOCRStatus = createSmartAction<RequestAction<boolean>, PollOCRStatusPayload>(
  'files/pollOCRStatus',
  (fileIds, documentType) => {
    const params = qs.stringify({ fileIds }, { indices: false });
    return {
      request: {
        url: `/files/status?${params}`,
        method: 'GET',
      },
      meta: {
        poll: 2,
        errorMessage: 'There was an error while processing your files. Check your files, and please try again.',
        onSuccess: (response: QueryState<boolean>, _requestAction, store) => {
          if (response.data) {
            store.dispatch(stopPolling([pollOCRStatus.toString()]));
            const state = store.getState();
            const ocrStatus = getOCRStatus(state);
            // check to see of ocr has been cancelled
            if (ocrStatus[documentType] === FileStatus.inprogress) {
              store.dispatch(files.actions.setOCRStatus({ documentType, status: FileStatus.completed }));
              store.dispatch(fetchOCR(fileIds[0], documentType));
            }
          }
          return response;
        },
        onError: (error: AxiosError, _requestAction, store) => {
          store.dispatch(stopPolling([pollOCRStatus.toString()]));
          store.dispatch(files.actions.setOCRStatus({ documentType, status: FileStatus.error }));
          return error;
        },
      },
    };
  },
);

type StartOCRPayload = [string, DocumentType, string];
export const startOCR = createSmartAction<RequestAction<APIStartOCR>, StartOCRPayload>(
  'request/startOCR',
  (fileId, documentType, inspectionId) => ({
    payload: {
      request: {
        url: `/files/ocr/${fileId}/${inspectionId}`,
        method: 'POST',
        data: { fileId, documentType },
      },
    },
    meta: {
      onSuccess: (response: QueryState<APIStartOCR>, _requestAction, store) => {
        if (isInspectionUrl()) {
          const file = response.data;
          store.dispatch(files.actions.setOCRStatus({ documentType, status: FileStatus.inprogress }));
          store.dispatch(pollOCRStatus([file.id], documentType));
        }
        return response;
      },
      onError: (error: AxiosError, _action, store) => {
        store.dispatch(files.actions.setOCRStatus({ documentType, status: FileStatus.error }));
        return error;
      },
    },
  }),
);

type StartShapesJobPayload = [
  string,
  DocumentType,
  'Layer' | 'Separation' | 'SnapToContent',
  string?,
  { id: string; top: number; right: number; bottom: number; left: number; pageNumber: number }?,
];

export const startShapesJob = createSmartAction<RequestAction<any>, StartShapesJobPayload>(
  'request/startShapesJob',
  (fileId, documentType, type, layerOrSeparationName?, crop?) => {
    store.dispatch(files.actions.setShapesJobStatus(FileStatus.inprogress));

    const state = store.getState();
    const layers = state.inspection[documentType].layers;
    const separations = state.inspection[documentType].separations;
    const overprint = state.inspection[documentType].overprint;

    const data: any = { type, layers, separations, overprint };

    if (layerOrSeparationName && type !== 'SnapToContent') {
      data.layerOrSeparationName = layerOrSeparationName;
    }

    if (type === 'SnapToContent') {
      data.crop = {
        top: crop?.top,
        right: crop?.right,
        bottom: crop?.bottom,
        left: crop?.left,
        pageNumber: crop?.pageNumber,
      };
    }

    return {
      payload: {
        request: {
          url: `/files/shapes/${fileId}`,
          method: 'POST',
          data,
        },
      },
      meta: {
        onSuccess: (response: QueryState<any>, _requestAction, store) => {
          const job = response.data;
          store.dispatch(pollShapesJobStatus(job?.id, documentType, fileId, type, crop?.id));
          return response;
        },
        onError: (error: AxiosError, _action, store) => {
          store.dispatch(files.actions.setShapesJobStatus(FileStatus.error));
          return error;
        },
      },
    };
  },
);

type PollShapesJobStatusPayload = [string, DocumentType, string, 'Layer' | 'Separation' | 'SnapToContent', string?];
const pollShapesJobStatus = createSmartAction<RequestAction<boolean>, PollShapesJobStatusPayload>(
  'files/pollShapesJobStatus',
  (taskId, documentType, fileId, type, cropId?) => {
    return {
      request: {
        url: `/files/shapes/status/${taskId}`,
        method: 'GET',
      },
      meta: {
        poll: 2,
        errorMessage: 'Something went wrong. Please try again.',
        onSuccess: (response: QueryState<boolean>, _requestAction, store) => {
          const shapesJobStatus = getShapesJobStatus(store.getState());
          if (shapesJobStatus === FileStatus.cancelled) {
            store.dispatch(stopPolling([pollShapesJobStatus.toString()]));
            store.dispatch(
              inspection.actions.setPolygons({ documentType, points: '', key: '', layerOrSeparationName: '' }),
            );
          } else if (response.data) {
            store.dispatch(stopPolling([pollShapesJobStatus.toString()]));
            store.dispatch(fetchShapesResponse(documentType, taskId, type, cropId));
          }
          return response;
        },
        onError: (error: AxiosError, _requestAction, store) => {
          console.log({ error });
          store.dispatch(stopPolling([pollShapesJobStatus.toString()]));
          store.dispatch(files.actions.setShapesJobStatus(FileStatus.error));
          return error;
        },
      },
    };
  },
);

type APIFetchShapeResponse = any;
type FetchShapeResponsePayload = [DocumentType, string, 'Layer' | 'Separation' | 'SnapToContent', string?];
const fetchShapesResponse = createSmartAction<RequestAction<APIFetchShapeResponse>, FetchShapeResponsePayload>(
  'request/fetchShapesResponse',
  (documentType, taskId, type, cropId) => ({
    payload: {
      request: {
        url: `files/shapes/${taskId}`,
        method: 'GET',
      },
    },
    meta: {
      errorMessage: 'There was an error. Please try again.',
      onSuccess: async (response: QueryState<APIFetchShapeResponse>, _requestAction, store) => {
        if (response.data.status === 500 || response.data.message) {
          store.dispatch(app.actions.setSnackMessage({ message: 'Something went wrong', type: 'error' }));
          store.dispatch(files.actions.setShapesJobStatus(FileStatus.error));
          console.error(response.data.message || 'Fetch Shapes Response Error');
          return;
        }
        if (type !== 'SnapToContent') {
          const points = response.data;
          if (!points || points.length === 0 || points[0]?.length === 0) {
            store.dispatch(app.actions.setSnackMessage({ message: 'No shapes found for this layer', type: 'info' }));
          } else {
            store.dispatch(inspection.actions.setPolygons({ documentType, points }));
          }
          store.dispatch(files.actions.setShapesJobStatus(FileStatus.completed));
        } else {
          const annotManager = PDFAnnotationManager.getInstance(documentType);
          if (!annotManager || !cropId) return;
          const docManager = PDFManagerFactory.getPDFDocManager();
          if (!docManager) return;
          docManager.updateCropWithSnap(cropId, documentType, response.data);
          store.dispatch(files.actions.setShapesJobStatus(FileStatus.completed));
        }
        return response;
      },
      onError: (error: AxiosError, _action, store) => {
        console.log('error!', { error });
        store.dispatch(files.actions.setShapesJobStatus(FileStatus.error));
      },
    },
  }),
);

// ==========================================
// Actions for adding more files within ReorderFiles modal
// These actions don't reset fetchMultipleFiles data
// ==========================================

type AddMoreFilesData = {
  uploadedFiles: UploadedFile[];
  failedFiles: FailedFile[];
};

type FetchAddMoreFilesPayload = [
  string[], // uploaded file ids
  FailedFile[], // files rejected from the client
];
export const fetchAddMoreFiles = createSmartAction<
  RequestAction<ApiFetchMultipleFiles, AddMoreFilesData | undefined>,
  FetchAddMoreFilesPayload
>('files/fetchAddMoreFiles', (fileUploadIds, rejectedFiles) => {
  const fileParams = qs.stringify({ fileIds: fileUploadIds }, { indices: false });
  // Use a unique request key to allow concurrent fetches
  const requestKey = fileUploadIds.join('-');

  return {
    request: {
      url: `/files?${fileParams}`,
      method: 'GET',
    },
    meta: {
      requestKey,
      asMutation: false,
      errorMessage: 'There was an error uploading files. Please try again.',
      getData: (data) => {
        const uploadedFiles: UploadedFile[] = [];
        const failedFiles: FailedFile[] = [...rejectedFiles];
        data.forEach((file) => {
          if (file.status === FileStatus.error) {
            const failedFile: FailedFile = {
              id: file.id,
              filename: file.originalFilename,
              error: file.error || FileErrors.uploadError,
            };
            failedFiles.push(failedFile);
          } else {
            const uploadedFile: UploadedFile = {
              id: file.id,
              originalFilename: file.originalFilename,
              originalPath: file.originalPath,
              filename: file.filename,
              path: file.path,
              signedUrl: file.signedUrl,
              createdAt: file.createdAt,
              updatedAt: file.updatedAt,
              status: file.status,
            };
            uploadedFiles.push(uploadedFile);
          }
        });
        return { uploadedFiles, failedFiles };
      },
      onSuccess: (response: QueryState<AddMoreFilesData>, _action, store) => {
        // Dispatch to the ready files queue for the component to process
        if (response.data) {
          const { cancelledFileIds, cancelledFilenames } = store.getState().files;

          // Filter out cancelled files by ID or filename
          const filteredData: AddMoreFilesData = {
            uploadedFiles: response.data.uploadedFiles.filter(
              (f) => !cancelledFileIds.includes(f.id) && !cancelledFilenames.includes(f.originalFilename),
            ),
            failedFiles: response.data.failedFiles.filter(
              (f) => !cancelledFileIds.includes(f.id) && !cancelledFilenames.includes(f.filename),
            ),
          };

          // Only add to queue if there are files left
          if (filteredData.uploadedFiles.length > 0 || filteredData.failedFiles.length > 0) {
            store.dispatch(files.actions.addReadyFiles(filteredData));
          }

          // Clean up the file-to-batch mapping for these files
          const allFileIds = [
            ...response.data.uploadedFiles.map((f) => f.id),
            ...response.data.failedFiles.map((f) => f.id),
          ];
          store.dispatch(files.actions.removeFromFileToBatchMap(allFileIds));
        }
        return response;
      },
      onError: (error: AxiosError, _action, store) => {
        // Mark these files as cancelled so they won't appear
        fileUploadIds.forEach((id: string) => {
          store.dispatch(files.actions.addCancelledFileId(id));
        });
        return error;
      },
    },
  };
});

type PollAddMoreFilesStatusPayload = [
  string[], // uploaded file ids
  FailedFile[], // rejected files
];
export const pollAddMoreFilesStatus = createSmartAction<RequestAction<boolean>, PollAddMoreFilesStatusPayload>(
  'files/pollAddMoreFilesStatus',
  (fileUploadIds, rejectedFiles) => {
    const fileParams = qs.stringify({ fileIds: fileUploadIds }, { indices: false });
    // Use a unique request key based on the file IDs to allow concurrent polling
    const requestKey = fileUploadIds.join('-');
    const pollingQuery = pollAddMoreFilesStatus.toString() + requestKey;

    return {
      request: {
        url: `/files/status?${fileParams}`,
        method: 'GET',
      },
      meta: {
        requestKey,
        poll: 2,
        errorMessage: 'There was an error uploading files. Please try again.',
        onError: (error: AxiosError, _action, store) => {
          store.dispatch(stopPolling([pollingQuery]));
          // Mark these files as cancelled so they won't appear
          fileUploadIds.forEach((id: string) => {
            store.dispatch(files.actions.addCancelledFileId(id));
          });
          return error;
        },
        onSuccess: (response: QueryState<ApiPollFilesStatus>, _requestAction, store) => {
          if (response.data === true) {
            store.dispatch(stopPolling([pollingQuery]));
            // Check if this entire batch was cancelled
            if (!store.getState().files.cancelledBatchKeys.includes(requestKey)) {
              store.dispatch(fetchAddMoreFiles(fileUploadIds, rejectedFiles));
            }
          }
          return response;
        },
      },
    };
  },
);

export const finishAddMoreFilesCreation = createSmartAction<
  RequestAction<{ files: { id: string; status: number }[]; rejectedFiles: FailedFile[] }>
>('files/finishAddMoreFilesCreation', (uploadedFiles, rejectedFiles) => {
  const fileIds = uploadedFiles.map((file: any) => file.id);
  const batchKey = fileIds.join('-');

  return {
    payload: {
      request: {
        url: `/files/finishBatchFileCreation`,
        method: 'PATCH',
        data: {
          files: uploadedFiles,
        },
      },
    },
    meta: {
      errorMessage: 'There was an error finishing the file creation. Please try again.',
      onSuccess: (_response: any, _requestAction: any, store: Store) => {
        // Check if this entire batch was cancelled
        if (store.getState().files.cancelledBatchKeys.includes(batchKey)) {
          return;
        }
        store.dispatch(pollAddMoreFilesStatus(fileIds, rejectedFiles));
      },
      onError: (error, _action, store) => {
        // Mark these files as cancelled so they won't appear
        fileIds.forEach((id: string) => {
          store.dispatch(files.actions.addCancelledFileId(id));
        });
        return error;
      },
    },
  };
});

type AddMoreFilesPayload = [
  FormData, // accepted files
  FailedFile[], // rejected files
  PendingFileMapping[], // mapping of temp IDs to filenames for updating pending files
  boolean, // removeHeadersFooters
];
export const startAddMoreFilesCreation = createSmartAction<RequestAction<ApiUploadMultipleFiles>, AddMoreFilesPayload>(
  'files/startAddMoreFilesCreation',
  (acceptedFiles, rejectedFiles, pendingMapping, removeHeadersFooters) => {
    const filesData = (acceptedFiles.getAll('files') || []) as File[];
    const checksums = (acceptedFiles.getAll('checksums') || []) as string[];
    const data = filesData.map((file, i) => {
      const fileName = file instanceof File ? file.name : '';
      const fileType = file instanceof File ? file.type : '';
      return {
        fileName,
        fileType,
        checksum: checksums[i],
        removeHeadersFooters,
      };
    });
    return {
      request: {
        url: `/files/startBatchFileCreation`,
        method: 'POST',
        data: { files: data },
      },
      meta: {
        errorMessage: 'There was an error uploading files. Please try again.',
        onRequest: (request, _action, store) => {
          // Reset only the addMoreFiles request, not the main fetchMultipleFiles
          store.dispatch(resetRequests([fetchAddMoreFiles.toString()]));

          // Remove these filenames from cancelled lists (in case user is re-uploading)
          const filenames = data.map((file) => file.fileName);
          store.dispatch(files.actions.removeCancelledFilenames(filenames));

          return request;
        },
        onSuccess: (response: any, _requestAction, store) => {
          const fileData = response.data;
          const fileIds = fileData.map((file: any) => file.id);
          const batchKey = fileIds.join('-');

          // Track which files belong to this batch
          store.dispatch(files.actions.setFileToBatchMap({ fileIds, batchKey }));

          // Create mapping from temp IDs to real IDs using the pending mapping
          const usedTempIds = new Set<string>();
          const idUpdates = fileData
            .map((apiFile: any) => {
              // Match by filename, but track which temp IDs we've used (for same-name files)
              const mapping = pendingMapping.find((m) => m.filename === apiFile.fileName && !usedTempIds.has(m.tempId));
              if (mapping) {
                usedTempIds.add(mapping.tempId);
                return {
                  tempId: mapping.tempId,
                  realId: apiFile.id,
                };
              }
              return null;
            })
            .filter(Boolean) as PendingIdUpdate[];

          // Dispatch ID updates to Redux so ReorderFiles can update its local state
          if (idUpdates.length > 0) {
            store.dispatch(files.actions.addPendingIdUpdates(idUpdates));
          }

          const awsUploadPromises = fileData.map((file: any, index: number) => {
            return axiosInstance.put(file.signedURL, filesData[index], {
              headers: {
                'Content-Type': data[index].fileType,
              },
            });
          });
          Promise.all(awsUploadPromises)
            .then((resp) => {
              // Check if this entire batch was cancelled
              if (store.getState().files.cancelledBatchKeys.includes(batchKey)) {
                return;
              }

              const { cancelledFileIds, cancelledFilenames } = store.getState().files;

              const uploadedFiles = resp
                .map((awsResp, index) => {
                  if (awsResp.status !== 200) {
                    rejectedFiles.push({
                      id: fileData[index].id,
                      filename: fileData[index].fileName,
                      error: FileErrors.uploadError,
                    });
                  }
                  return {
                    id: fileData[index].id,
                    status: awsResp.status,
                    filename: fileData[index].fileName,
                  };
                })
                .filter((file) => !cancelledFileIds.includes(file.id) && !cancelledFilenames.includes(file.filename)); // Filter out individually cancelled files by ID or filename

              // Only proceed if there are files left to finalize
              if (uploadedFiles.length > 0) {
                store.dispatch(finishAddMoreFilesCreation(uploadedFiles, rejectedFiles));
              }
            })
            .catch((error) => {
              // Handle S3 upload errors for Add More Files
              console.error('S3 upload failed (Add More Files):', error);
              // Mark files as cancelled so they won't appear when polling finishes
              fileIds.forEach((id: string) => {
                store.dispatch(files.actions.addCancelledFileId(id));
              });
              // Notify component to remove pending files using the mapping
              if (pendingMapping && pendingMapping.length > 0) {
                const tempIds = pendingMapping.map((m) => m.tempId);
                const filenames = pendingMapping.map((m) => m.filename);
                store.dispatch(files.actions.addFailedUpload({ fileIds: tempIds, filenames }));
              }
              store.dispatch(
                app.actions.setSnackMessage({
                  message: 'There was an error uploading files to storage. Please try again.',
                  type: 'error',
                }),
              );
            });
        },
        onError: (error, _action, store) => {
          // Handle errors from startBatchFileCreation API call
          // Notify component to remove pending files
          if (pendingMapping && pendingMapping.length > 0) {
            const tempIds = pendingMapping.map((m) => m.tempId);
            const filenames = pendingMapping.map((m) => m.filename);
            store.dispatch(files.actions.addFailedUpload({ fileIds: tempIds, filenames }));
          }
          store.dispatch(
            app.actions.setSnackMessage({
              message: 'There was an error starting the file upload. Please try again.',
              type: 'error',
            }),
          );
          return error;
        },
      },
    };
  },
);
